// ============================================================================
// marketMasterArea.ts — ЗОНА ОБСЛУЖИВАНИЯ МАСТЕРА (кабинет мастера).
//
// Мастер редактирует СВОЮ карточку mkt_masters (entity_id = текущий
// пользователь): страна/регион/город/район, координаты и радиус обслуживания.
// Пишутся только гео-реквизиты — остальные поля карточки (имя/телефон/о
// мастере/проверка) не затрагиваются.
//
// SSoT — сервер: гео-значения проверяются markettake/geo (существование
// справочных записей, иерархия, диапазоны координат, радиус > 0). Клиент
// делает ту же проверку ДО отправки, чтобы ошибка была понятной сразу.
// ============================================================================

import { dataService } from '@/lib/dataService';
import { MARKET_MASTER_TABLE, cellsOf, listTableRows } from '@/lib/market';
import {
  marketGeoFromCells,
  marketGeoToValue,
  marketGeoToValues,
  validateMarketGeo,
  type MarketGeoValue,
} from '@/lib/marketGeo';

export interface MarketMasterProfile {
  id: string;
  name: string;
  phone: string;
  about: string;
  verified: boolean;
  /** Средняя оценка (только опубликованные отзывы). */
  rating: number | null;
  /** Количество опубликованных отзывов. */
  reviewsCount: number | null;
  /** Гео-значение для формы (страна/регион/город/район, координаты, радиус). */
  geo: MarketGeoValue;
}

function isTrue(value: string | undefined): boolean {
  const s = String(value ?? '').trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'да';
}

function numberOf(value: string | undefined): number | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const parsed = Number(raw.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

/** Карточка мастера текущего пользователя (null — профиля мастера нет). */
export async function loadMyMarketMaster(userId: string): Promise<MarketMasterProfile | null> {
  if (!userId) throw new Error('Не определён текущий пользователь — войдите заново');
  const rows = await listTableRows(MARKET_MASTER_TABLE, {
    pageSize: 1,
    filters: [{ column: 'entity_id', value: userId }],
  });
  const row = rows[0];
  if (!row) return null;
  const cells = cellsOf(row);
  return {
    id: row.id,
    name: cells.get('name') || '',
    phone: cells.get('phone') || '',
    about: cells.get('about') || '',
    verified: isTrue(cells.get('verified')),
    rating: numberOf(cells.get('rating_avg')),
    reviewsCount: numberOf(cells.get('reviews_count')),
    geo: marketGeoToValue(marketGeoFromCells(cells)),
  };
}

/** Гео мастера (для отборов ленты); null — профиля нет. */
export async function loadMyMarketGeo(userId: string): Promise<MarketGeoValue | null> {
  const master = await loadMyMarketMaster(userId);
  return master ? master.geo : null;
}

/**
 * Сохранить зону обслуживания мастера. Пустые значения очищают гео
 * (сервер принимает пустое гео как «не задано»). Ошибка сервера/прав
 * пробрасывается — интерфейс покажет её текстом.
 */
export async function saveMarketMasterGeo(
  userId: string,
  geo: MarketGeoValue,
): Promise<void> {
  if (!userId) throw new Error('Не определён текущий пользователь — войдите заново');
  const error = validateMarketGeo(geo);
  if (error) throw new Error(error);
  const master = await loadMyMarketMaster(userId);
  if (!master) {
    throw new Error('Профиль мастера не найден — обратитесь к оператору маркетплейса');
  }
  await dataService.update('public', MARKET_MASTER_TABLE, master.id, marketGeoToValues(geo));
}
